import { buildPlaylistUrl } from '@apm/bloom-receiver-sdk';
import type { PlaylistResponse, PlaylistResume } from '@apm/contracts';

/**
 * **돌아왔을 때 이어보기를 다시 맞춘다** (v18).
 *
 * ── 무엇이 없어서 생긴 자리인가 ────────────────────────────────
 *
 * 이어보기는 **플레이리스트를 받을 때** 적용되고, 리시버는 채널·토큰·컨테이너가 바뀔 때만
 * 다시 선다(`useReceiver.ts` 의 그 의존성 배열). 그래서 **앱을 켜 둔 채 다른 기기를 보다가
 * 돌아오면** 아무것도 다시 묻지 않는다 — 화면은 떠날 때 그 자리 그대로다.
 *
 * 2026-08-19 기록으로 확인된 모양이 정확히 그것이다: 아이폰이 06:33 에 `start` 를 낸 뒤
 * **하트비트 말고는 서버에 아무것도 요청하지 않았고**, 그 사이 노트북이 09:10 에 이어보기
 * 지점을 4회차 240초로 옮겼다. 아이폰→노트북은 노트북이 **앱을 새로 열어서** 됐고,
 * 노트북→아이폰은 아이폰이 **물어본 적이 없어서** 안 됐다.
 *
 * ── 판정이 왜 순수 함수인가 ────────────────────────────────────
 *
 * 이 저장소에는 jsdom 이 없어 컴포넌트를 렌더해 재는 방법이 없다(`watch-progress.ts` 와
 * 같은 사정). **움직일지 말지가 이 파일에 있어야** node 환경 테스트가 그것을 본다.
 */

/**
 * 이 초 이하의 차이로는 움직이지 않는다.
 *
 * **주기 보고가 10초이므로 남의 기기가 쓴 값도 최대 10초쯤 낡아 있다.** 그 폭 안의 차이는
 * 「남이 앞서갔다」와 「내 마지막 보고가 조금 뒤처졌다」를 가를 수 없고, 가르지 못하는
 * 차이로 화면을 다시 세우면 **탭을 오갈 때마다 영상이 처음부터 적재된다.**
 */
export const RESUME_RETURN_MIN_GAP_SEC = 15;

/**
 * 탐침의 최소 간격(ms).
 *
 * **`focus` 를 함께 듣기 때문에 필요하다** — 그 사건은 화면 안을 클릭하기만 해도 온다.
 * 진짜 복귀는 대개 분 단위 뒤라 이 바닥에 걸리지 않고, 걸리는 것은 연달아 오는 중복뿐이다
 * (한 번의 복귀에 `visibilitychange`·`focus` 가 **둘 다** 오는 것이 흔하다).
 */
export const RESUME_PROBE_MIN_INTERVAL_MS = 10_000;

/**
 * 마지막 확인의 기록. **`?debug=1` 패널과 하네스가 읽는다.**
 *
 * **이것이 없으면 실패를 가를 수 없다** — 「사건이 오지 않았다」와 「와서 안 움직이기로
 * 했다」가 화면에서 똑같이 **아무 일도 없음**으로 보인다. 2026-08-19 에 실제로 그 둘을
 * 놓고 헤맸고, 답은 **아이폰이 옛 번들을 돌고 있었다**였다.
 */
export interface ResumeCheckLog {
  atMs: number;
  /** 무엇이 깨웠는가 */
  by: 'visibility' | 'pageshow' | 'focus';
  /** 서버가 말한 지점. 없으면 `null` */
  serverResume: PlaylistResume | null;
  /** 그때 내 자리 */
  elapsedSec: number;
  currentProductId: string | null;
  /** 옮겼는가 */
  moved: boolean;
}

export interface ReturnSnapshot {
  /** 일반 채널이 아니면 이어보기가 없다 — 홈쇼핑은 서버가 언제나 `null` 을 준다 */
  isGeneral: boolean;
  /** 비로그인은 이어보기가 없다 (v18 규칙 ③) */
  loggedIn: boolean;
  /** 진입 게이트를 지나 재생이 시작됐는가. 아직이면 곧 `start()` 가 돌면서 알아서 맞춰진다 */
  started: boolean;
  /** 지금 재생 중인 회차 */
  currentProductId: string | null;
  /** 지금 회차 안에서 몇 초 (`receiver.getElapsedSec()`) */
  elapsedSec: number;
  /** 서버가 방금 말해 준 이어볼 지점 */
  serverResume: PlaylistResume | null;
  /**
   * v21. **지금 광고 구간인가.**
   *
   * ★ **광고 중에는 절대 옮기지 않는다.** `resync()` 는 `start()` 를 다시 부르는 것이라
   * pod 이 통째로 사라지고 회차가 처음(또는 이어보기 지점)부터 다시 적재된다 — **시청자
   * 눈에는 「광고 보다가 화면이 튀었다」**이고, 그 순간 임프레션도 함께 잃는다.
   *
   * 그리고 `getElapsedSec()` 이 **광고의 초**라 위 비교가 애초에 성립하지 않는다 —
   * 15초짜리 광고의 3초가 회차의 3초로 읽혀 **거의 언제나 「서버가 앞섰다」**가 된다.
   *
   * **화면에 `if` 를 넣지 않는다** — 여기 두어야 node 테스트가 그 갈래를 본다.
   */
  inAdBreak: boolean;
  /**
   * v33. **지금 라이브 바이패스인가** (절대 규칙 83 · `docs/67` D27).
   *
   * ★ **바이패스 중에는 옮기지 않는다.** 라이브는 **정의상 엣지에 있어** 맞출 것이
   * 없고, `resync()` 는 `start()` 를 다시 부르는 것이라 **스트림을 통째로 다시
   * 적재한다** — 시청자 눈에는 「보다가 화면이 한 번 끊겼다」다. 그리고 그 사이에
   * 두고 온 자리(`returnTo`)가 위태로워진다.
   *
   * **`inAdBreak` 과 따로 둔다**(D25) — 이유가 같아도(경과가 콘텐츠의 초가 아니다)
   * **끊는 자리는 둘이다.** 한 칸으로 겸하면 이름이 거짓이 된다.
   */
  inLive: boolean;
}

/**
 * 돌아온 화면을 서버 지점으로 옮길 것인가.
 *
 * **뒤로는 가지 않는다.** 서버의 초가 내 경과보다 작으면 그 값을 쓴 것은 **나 자신**일
 * 가능성이 크다 — 탭이 숨은 동안에도 데스크톱은 영상을 계속 돌리므로, 숨기 직전에 보고한
 * 값이 서버에 있고 나는 그보다 앞서 있다. 그때 서버 값을 따르면 **돌아올 때마다 영상이
 * 뒤로 감긴다.** 앞선 쪽이 이기게 두는 것이 한 지점 모델에서 안전한 방향이다.
 *
 * **회차가 다르면 초를 보지 않고 따른다.** 다른 기기가 다른 회차로 옮겼다는 뜻이고,
 * 그것이 「계정 하나에 지점 하나」의 요점이다.
 */
export function shouldResync(now: ReturnSnapshot): boolean {
  if (!now.isGeneral || !now.loggedIn || !now.started) return false;
  // v21. **광고 중에는 옮기지 않는다** (`ReturnSnapshot.inAdBreak` 주석).
  if (now.inAdBreak) return false;
  // v33. **바이패스 중에도 옮기지 않는다** (`ReturnSnapshot.inLive` 주석).
  if (now.inLive) return false;
  if (now.currentProductId === null || now.serverResume === null) return false;

  if (now.serverResume.productId !== now.currentProductId) return true;
  return now.serverResume.positionSec > now.elapsedSec + RESUME_RETURN_MIN_GAP_SEC;
}

/**
 * 서버의 이어보기 지점만 읽어 본다. **탐침이고 아무것도 바꾸지 않는다.**
 *
 * **새 조회 경로를 만들지 않는다** (절대 규칙 34) — 리시버가 쓰는 그 플레이리스트
 * 엔드포인트 그대로이고, URL 도 SDK 의 `buildPlaylistUrl` 로 만들어 **`deviceId` 가 빠질
 * 자리를 없앤다** (절대 규칙 2).
 *
 * **리시버의 `PlaylistClient` 를 쓰지 않는 이유**가 하나 있다. 그쪽으로 받으면 마지막 응답이
 * 교체되면서 **`playlistId` 가 바뀌는데**, 움직이지 않기로 한 경우에도 바뀐다 — 그러면 지금
 * 재생 중인 회차의 이후 이벤트가 **틀지도 않은 목록에 귀속된다.** 그래서 탐침은 바깥에서
 * 따로 받고, 실제로 옮길 때만 `receiver.resync()` 가 한 번 더 받는다.
 *
 * 실패는 조용하다 — 이어보기를 못 맞추는 것이 재생을 멈출 이유가 되지 않는다
 * (절대 규칙 7·14 의 승계).
 */
export async function fetchResumePoint(
  serverUrl: string,
  channelId: string,
  deviceId: string,
  authToken: string | null,
): Promise<PlaylistResume | null> {
  try {
    const res = await fetch(buildPlaylistUrl(serverUrl, channelId, deviceId), {
      headers: authToken === null ? {} : { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as PlaylistResponse;
    return body.resume ?? null;
  } catch {
    return null;
  }
}
